import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, LANGS } from "../i18n/LanguageContext";

const Logo = ({ onClick }) => (
  <Link to="/" onClick={onClick} data-testid="nav-logo" className="flex flex-col items-start leading-none group">
    <span className="font-serif text-2xl md:text-[1.7rem] tracking-[0.35em] text-kenosis-ivory pl-[0.35em] transition-colors group-hover:text-kenosis-gold">
      KENOSIS
    </span>
    <span className="font-sans text-[0.55rem] tracking-[0.55em] text-kenosis-gold pl-[0.55em] mt-1">
      METHOD
    </span>
  </Link>
);

const LangSwitcher = ({ compact, testIdPrefix = "lang" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-3 ${compact ? "" : ""}`} data-testid="language-switcher">
      {LANGS.map((l) => (
        <button
          key={l.code}
          data-testid={`${testIdPrefix}-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`relative font-sans text-xs tracking-[0.15em] transition-colors ${
            lang === l.code ? "text-kenosis-gold" : "text-kenosis-muted hover:text-kenosis-ivory"
          }`}
        >
          {l.label}
          {lang === l.code && (
            <motion.span layoutId={`${testIdPrefix}Underline`} className="absolute -bottom-1 left-0 right-0 h-px bg-kenosis-gold" />
          )}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while mobile menu is open so page content doesn't bleed through
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const html = document.documentElement;
    const prev = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const links = [
    { to: "/philosophy", label: t("nav.philosophy") },
    { to: "/method", label: t("nav.method") },
    { to: "/services", label: t("nav.services") },
    { to: "/founder", label: t("nav.founder") },
    { to: "/journal", label: t("nav.journal") },
  ];

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled || open ? "bg-kenosis-black/90 backdrop-blur-md border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-link-${l.to.slice(1)}`}
                  className={`relative font-sans text-xs uppercase tracking-[0.2em] transition-colors ${
                    active ? "text-kenosis-gold" : "text-kenosis-ivory/80 hover:text-kenosis-ivory"
                  }`}
                >
                  {l.label}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center gap-8">
            <LangSwitcher />
            <button
              data-testid="nav-cta-button"
              onClick={() => navigate("/apply")}
              className="group relative overflow-hidden border border-kenosis-gold/60 px-6 py-3"
            >
              <span className="relative z-10 font-sans text-[0.65rem] uppercase tracking-[0.2em] text-kenosis-gold transition-colors duration-500 group-hover:text-kenosis-black">
                {t("nav.cta")}
              </span>
              <span className="absolute inset-0 z-0 origin-left scale-x-0 bg-kenosis-gold transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-kenosis-ivory"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-kenosis-black/80 backdrop-blur-2xl lg:hidden flex flex-col"
          >
            <div className="flex-1 flex flex-col justify-center px-8 gap-6 pt-24">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.to.slice(1)}`}
                    className="font-serif text-4xl font-light text-kenosis-ivory text-outline-strong"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <button
                data-testid="mobile-cta-button"
                onClick={() => navigate("/apply")}
                className="mt-6 self-start border border-kenosis-gold/70 bg-kenosis-black/60 px-7 py-4 font-sans text-xs uppercase tracking-[0.2em] text-kenosis-gold backdrop-blur-md"
              >
                {t("nav.cta")}
              </button>
            </div>
            <div className="px-8 pb-12">
              <LangSwitcher testIdPrefix="lang-mobile" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
